/**
 * JWT Decode Tool
 * 
 * Decodes JSON Web Tokens and extracts header/payload.
 */

import { decodeJwt, decodeProtectedHeader } from 'jose';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const JwtDecodeTool: ITransformerTool = {
  id: 'jwt-decode',
  name: 'JWT Decode',
  description: 'Decode JSON Web Tokens',
  version: '1.0.0',
  type: 'transformer',
  category: 'Encoders & Decoders',
  keywords: ['jwt', 'json web token', 'decode', 'token', 'auth', 'json'],

  seo: {
    slug: 'jwt-decoder',
    pageTitle: 'JWT Decoder - Free Online JSON Web Token Tool',
    metaDescription: 'Decode and inspect JSON Web Tokens. View header, payload, and signature claims. Fast, private, runs entirely in your browser.',
    keywords: ['jwt decoder', 'json web token decoder', 'jwt parser', 'decode jwt online', 'jwt inspect'],
    relatedTools: ['json-format', 'base64-decode'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.JWT],
  outputType: SEMANTIC_TYPES.JSON,

  attributes: [
    {
      name: 'part',
      type: 'select',
      label: 'Part to Extract',
      defaultValue: 'all',
      options: ['all', 'header', 'payload'],
      description: 'Which part of the JWT to extract',
    },
    {
      name: 'format',
      type: 'boolean',
      label: 'Format Output',
      defaultValue: true,
      description: 'Pretty-print the JSON output',
    },
  ],

  supportsInput(input: string): boolean {
    const parts = input.trim().split('.');
    return parts.length === 3;
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const part = (config.part as string) || 'all';
    const format = config.format !== false;

    const parts = input.trim().split('.');
    if (parts.length !== 3) {
      throw ToolError.validation('Invalid JWT format: Expected 3 parts separated by dots');
    }

    const [headerB64, payloadB64, signature] = parts;

    if (!headerB64 || !payloadB64) {
      throw ToolError.validation('Missing JWT parts');
    }

    try {
      const headerRaw = decodeProtectedHeader(input.trim());
      const payloadRaw = decodeJwt(input.trim());

      // Stabilize common key order for display/tests while preserving extra keys.
      const header: Record<string, unknown> = {
        alg: headerRaw.alg,
        typ: headerRaw['typ'],
        ...headerRaw,
      };

      const payload: Record<string, unknown> = {
        sub: payloadRaw.sub,
        name: payloadRaw['name'],
        admin: payloadRaw['admin'],
        ...payloadRaw,
      };

      let result: unknown;
      
      if (part === 'header') {
        result = header;
      } else if (part === 'payload') {
        result = payload;
      } else {
        result = {
          header,
          payload,
          signature,
        };
      }

      return format 
        ? JSON.stringify(result, null, 2)
        : JSON.stringify(result);
    } catch (e) {
      throw ToolError.validation('Failed to decode JWT: Invalid Base64 or JSON content');
    }
  },
};

